import { getMatchDurationFromConstraints } from '../../pages/tourDetails/Schedule/components/schedule.helpers';
import {
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
  isFreeSlot,
} from '../../types/schedule';

/** Shared with UI and Redux schedule actions — keep preview and persisted overlap logic aligned. */
export const MS_PER_MINUTE = 60000;

export const wouldOverlap = (
  proposedStartMs: number,
  durationMins: number,
  courtId: number,
  excludeMatchIds: number[],
  allMatches: IMatchScheduleItem[],
  constraints: IScheduleConstraints
): boolean => {
  const courtMatches = allMatches.filter((m) => m.court?.id === courtId);
  return wouldOverlapOnCourt(
    proposedStartMs,
    durationMins,
    courtId,
    new Set(excludeMatchIds),
    courtMatches,
    constraints
  );
};

export const wouldOverlapOnCourt = (
  proposedStartMs: number,
  durationMins: number,
  _courtId: number,
  excludeIds: Set<number>,
  courtMatches: IMatchScheduleItem[],
  constraints: IScheduleConstraints
): boolean => {
  const proposedEndMs = proposedStartMs + durationMins * MS_PER_MINUTE;

  for (const m of courtMatches) {
    if (excludeIds.has(m.id)) continue;
    if (!m.scheduleDate) continue;

    const startMs = new Date(m.scheduleDate).getTime();
    const duration = m.duration || getMatchDurationFromConstraints(m, constraints) || 30;
    const endMs = startMs + duration * MS_PER_MINUTE;

    if (proposedStartMs < endMs && proposedEndMs > startMs) return true;
  }
  return false;
};

/** Pending (unscheduled) match fits in the target scheduled slot when replacing that row (excludes target id only). */
export const canPlacePendingMatchAtScheduledSlot = (
  pending: IMatchScheduleItem,
  target: IMatchScheduleItem,
  allMatches: IMatchScheduleItem[],
  constraints: IScheduleConstraints
): boolean => {
  if (!target.scheduleDate || !target.court) return false;
  const pendingDuration =
    pending.duration || getMatchDurationFromConstraints(pending, constraints) || 30;
  const proposedStartMs = new Date(target.scheduleDate).getTime();
  return !wouldOverlap(
    proposedStartMs,
    pendingDuration,
    target.court.id,
    [target.id],
    allMatches,
    constraints
  );
};

/** Same validity as `swapScheduleItems` for match-vs-match swaps. */
export const canSwapTwoScheduledMatches = (
  source: IMatchScheduleItem,
  target: IMatchScheduleItem,
  allMatches: IMatchScheduleItem[],
  constraints: IScheduleConstraints
): boolean => {
  if (!target.scheduleDate || !target.court || !source.scheduleDate || !source.court) return false;
  if (source.id === target.id) return true;
  const sourceDuration =
    source.duration || getMatchDurationFromConstraints(source, constraints) || 30;
  const targetStartMs = new Date(target.scheduleDate).getTime();
  if (
    wouldOverlap(
      targetStartMs,
      sourceDuration,
      target.court.id,
      [source.id, target.id],
      allMatches,
      constraints
    )
  ) {
    return false;
  }
  const targetDuration =
    target.duration || getMatchDurationFromConstraints(target, constraints) || 30;
  const sourceStartMs = new Date(source.scheduleDate).getTime();
  if (
    wouldOverlap(
      sourceStartMs,
      targetDuration,
      source.court.id,
      [source.id, target.id],
      allMatches,
      constraints
    )
  ) {
    return false;
  }
  return true;
};

export const flattenScheduledMatches = (
  schedule: ITournamentSchedule | null | undefined
): IMatchScheduleItem[] =>
  schedule?.days.flatMap((d) =>
    d.courts.flatMap((c) => c.matches.filter((m) => !isFreeSlot(m)) as IMatchScheduleItem[])
  ) ?? [];

export const dedupeScheduledMatchesById = (
  matches: IMatchScheduleItem[]
): IMatchScheduleItem[] => {
  const byId = new Map<number, IMatchScheduleItem>();
  for (const match of matches) {
    if (Number.isInteger(match.id)) {
      byId.set(match.id, match);
    }
  }
  return [...byId.values()];
};
